import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, ShieldAlert } from 'lucide-react';
import { formatDate } from '../../lib/api';
import { updateCita } from '../../lib/citasApi';
import { useAuth } from '../../lib/auth';
import { canForceSlot } from '../../lib/roles';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import FreeSlotPicker from './FreeSlotPicker';

/**
 * Mover una cita existente a otro hueco (misma agenda y concepto). La hora
 * puede ser un hueco libre o una tecleada a mano; si hay conflicto, el admin
 * puede forzar el solapamiento (mismo patrón que al crear cita).
 *
 * Props:
 *  - open / onOpenChange
 *  - cita:    la cita a mover ({id, agenda_id, concept_code, date, start, ...})
 *  - onMoved: callback tras mover con éxito
 */
export default function MoveCitaDialog({ open, onOpenChange, cita, onMoved }) {
  const { user } = useAuth();
  const admin = canForceSlot(user);
  const [slot, setSlot] = useState(null); // {date, start} | null
  const [dur, setDur] = useState(''); // duración (min) editable — tiempo de consulta
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(false); // 409 → ofrecer forzar (gestión agenda)
  // Cierre de DÍA COMPLETO del día que se está mirando en el selector.
  const [bloqueoDia, setBloqueoDia] = useState(null);

  useEffect(() => {
    if (!open) return;
    // Preseleccionamos el hueco actual: el picker excluye la propia cita,
    // así que su hueco aparece como libre y sirve de punto de partida.
    setSlot(cita ? { date: cita.date, start: cita.start } : null);
    setDur(cita ? String(cita.duration_minutes || '') : '');
    setSaving(false);
    setConflict(false);
    setBloqueoDia(null);
    // Dep por ID, no por objeto: el auto-refresco de la agenda cada 30 s crea
    // objetos `cita` nuevos y reseteaba el diálogo en plena selección
    // (revisión 2026-08-27).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cita?.id]);

  if (!cita) return null;

  const durNum = parseInt(dur, 10);
  const durChanged = Number.isFinite(durNum) && durNum > 0 && durNum !== cita.duration_minutes;
  const slotChanged = slot && (slot.date !== cita.date || slot.start !== cita.start);
  const isSameSlot = !slotChanged && !durChanged;
  // AGENDA BLOQUEADA EL DÍA ENTERO: a un día cerrado no se MUEVE una cita, ni
  // forzando (2026-09-17: la misma decisión que tapó el clic en la
  // columna roja de la agenda; quedaba esta otra puerta al mismo sitio). Si de
  // verdad hay que citar ese día, primero se quita el bloqueo.
  // Ojo: solo cuando la cita CAMBIA de día. Las citas que ya estaban ahí de
  // antes del bloqueo se siguen pudiendo ajustar dentro de su propio día.
  const diaDestinoBloqueado = !!bloqueoDia && !!slot && slot.date !== cita.date;

  const confirm = async (force = false) => {
    if (!slot || isSameSlot) return;
    if (diaDestinoBloqueado) {
      toast.error(`Agenda bloqueada: ${bloqueoDia.reason || 'sin motivo'}`, {
        description: 'Ese día no se pueden dar citas. Si hace falta, quita antes el bloqueo.',
      });
      return;
    }
    setSaving(true);
    try {
      const updated = await updateCita(cita.id, {
        date: slot.date,
        start: slot.start,
        ...(durChanged ? { duration_minutes: durNum } : {}),
        ...(force ? { force: true } : {}),
      });
      toast.success(`Cita movida al ${formatDate(slot.date)} a las ${slot.start}`);
      // Los Detalles nombran otra cirugía que el procedimiento (23/9/2026):
      // el servidor lo dice en `aviso_documentos` y aquí se enseña tal cual.
      if (updated?.aviso_documentos) toast.warning(updated.aviso_documentos, { duration: 12000 });
      onMoved?.(updated);
      onOpenChange(false);
    } catch (e) {
      if (e?.response?.status === 409) {
        // `forzable: false` = la paciente ya está citada en otro sitio a esa
        // hora (23/9/2026): «Forzar» no lo salta, así que no se ofrece.
        setConflict(e?.response?.data?.forzable !== false);
        toast.error(e?.response?.data?.detail || 'Ese hueco ya está ocupado');
      } else {
        toast.error(e?.response?.data?.detail || 'No se pudo mover la cita');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="move-cita-dialog">
        <DialogHeader>
          <DialogTitle>Mover cita</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border border-[#D9E3DD] bg-[rgba(46,110,94,0.06)] px-3 py-2">
            <div className="text-sm font-medium text-[#13231E]">{cita.patient_name}</div>
            <div className="text-xs text-muted-foreground">
              {cita.concept_name} · actualmente el {formatDate(cita.date)} a las {cita.start}
            </div>
          </div>

          <FreeSlotPicker
            agendaId={cita.agenda_id}
            conceptCode={cita.concept_code}
            excludeCitaId={cita.id}
            // Sin huecos que la dejen en dos sitios a la vez (23/9/2026).
            patientId={cita.patient_id}
            value={slot}
            onChange={(s) => { setSlot(s); setConflict(false); }}
            onDiaBloqueado={setBloqueoDia}
          />

          {/* Duración (tiempo de consulta): ajustar cuánto ocupa el hueco. */}
          <div className="flex items-center gap-2 flex-wrap">
            <label htmlFor="move-cita-dur" className="text-sm font-medium text-[#13231E]">Duración (min):</label>
            <Input
              id="move-cita-dur"
              type="number"
              min={5}
              step={5}
              value={dur}
              onChange={(e) => { setDur(e.target.value); setConflict(false); }}
              className="w-24"
              data-testid="move-cita-duration"
            />
            <span className="text-xs text-muted-foreground">ajusta el tiempo de la consulta / el hueco</span>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="move-cita-cancel">
            Cancelar
          </Button>
          {conflict && admin && !diaDestinoBloqueado && (
            <Button
              type="button"
              variant="outline"
              onClick={() => confirm(true)}
              disabled={saving}
              className="border-amber-500 text-amber-800 hover:bg-amber-50"
              data-testid="move-cita-force"
              title="Mover de todas formas aunque solape o esté fuera de horario"
            >
              <ShieldAlert className="w-4 h-4 mr-1.5" /> Forzar solapamiento
            </Button>
          )}
          <Button
            type="button"
            onClick={() => confirm(false)}
            disabled={!slot || isSameSlot || saving}
            className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
            data-testid="move-cita-confirm"
            title={diaDestinoBloqueado ? 'Agenda bloqueada ese día: quita antes el bloqueo' : undefined}
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {slot && !isSameSlot ? `Mover a ${formatDate(slot.date)} ${slot.start}` : 'Mover cita'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
